// src/modules/COGS/SpecialsTab.js
import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiCalendar, FiDollarSign, FiPercent } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import DatePicker from 'react-datepicker';
// import "react-datepicker/dist/react-datepicker.css";

const SpecialsTab = () => {
  const [specials, setSpecials] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedSpecial, setSelectedSpecial] = useState(null);
  const [isAddingSpecial, setIsAddingSpecial] = useState(false);

  useEffect(() => {
    // Mock data for specials
    setSpecials([
      {
        id: 1,
        name: "Burger Combo",
        items: ["DROVER BEEF BURGER", "DROVER REGULAR FRIES", "SOFTDRINKS"],
        regularPrice: 36.00,
        specialPrice: 32.00,
        startDate: new Date(2023, 5, 1),
        endDate: new Date(2023, 5, 30)
      },
      {
        id: 2,
        name: "Chicken Feast",
        items: ["DROVER CRISPY CHICKEN", "DROVER SPICY LEMON WINGS", "DROVER REGULAR FRIES"],
        regularPrice: 42.00,
        specialPrice: 38.00,
        startDate: new Date(2023, 6, 1),
        endDate: new Date(2023, 6, 31)
      }
    ]);

    // Mock data for items (from COGS)
    setItems([
      { id: 1, name: "DROVER BEEF BURGER", price: 21.74, cost: 7.94 },
      { id: 2, name: "DROVER CRISPY CHICKEN", price: 16.52, cost: 4.77 },
      { id: 3, name: "DROVER REGULAR FRIES", price: 5.22, cost: 1.70 },
      { id: 4, name: "SOFTDRINKS", price: 4.35, cost: 2.19 },
      { id: 5, name: "DROVER SPICY LEMON WINGS", price: 14.78, cost: 5.86 }
    ]);
  }, []);

  const calculateSpecialMetrics = (special) => {
    const regularPrice = special.items.reduce((sum, itemName) => {
      const item = items.find(i => i.name === itemName);
      return sum + (item ? item.price : 0);
    }, 0);

    const cost = special.items.reduce((sum, itemName) => {
      const item = items.find(i => i.name === itemName);
      return sum + (item ? item.cost : 0);
    }, 0);

    const discount = ((regularPrice - special.specialPrice) / regularPrice) * 100;
    const margin = ((special.specialPrice - cost) / special.specialPrice) * 100;

    return { regularPrice, cost, discount, margin };
  };

  const SpecialCard = ({ special, onEdit, onDelete }) => {
    const { regularPrice, cost, discount, margin } = calculateSpecialMetrics(special);

    return (
      <motion.div 
        layout
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
      >
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-xl font-semibold">{special.name}</h3>
          <div className="flex space-x-2">
            <button onClick={() => onEdit(special)} className="text-blue-500 hover:text-blue-700">
              <FiEdit2 size={18} />
            </button>
            <button onClick={() => onDelete(special.id)} className="text-red-500 hover:text-red-700">
              <FiTrash2 size={18} />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="font-medium">Regular Price: ${regularPrice.toFixed(2)}</p>
            <p className="font-medium">Special Price: ${special.specialPrice.toFixed(2)}</p>
            <p className="font-medium">Cost: ${cost.toFixed(2)}</p>
          </div>
          <div>
            <p className="font-medium">Discount: {discount.toFixed(2)}%</p>
            <p className="font-medium">Margin: {margin.toFixed(2)}%</p>
            <p className="font-medium">
              Duration: {special.startDate.toLocaleDateString()} - {special.endDate.toLocaleDateString()}
            </p>
          </div>
        </div>
        <div>
          <h4 className="font-medium mb-2">Items:</h4>
          <ul className="list-disc list-inside">
            {special.items.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      </motion.div>
    );
  };

  const SpecialForm = ({ special, onSubmit, onCancel }) => {
    const [formData, setFormData] = useState(special || {
      name: '',
      items: [],
      specialPrice: 0,
      startDate: new Date(),
      endDate: new Date()
    });
    const [calculatedMetrics, setCalculatedMetrics] = useState(null);

    const handleChange = (e) => {
      const value = e.target.type === 'number' ? parseFloat(e.target.value) : e.target.value;
      setFormData({ ...formData, [e.target.name]: value });
    };

    const handleItemToggle = (itemName) => {
      const newItems = formData.items.includes(itemName)
        ? formData.items.filter(i => i !== itemName)
        : [...formData.items, itemName];
      setFormData({ ...formData, items: newItems });
    };

    const handleCalculate = () => {
      const metrics = calculateSpecialMetrics(formData);
      setCalculatedMetrics(metrics);
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      onSubmit(formData);
    };

    return (
      <motion.form
        onSubmit={handleSubmit}
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className="bg-white p-6 rounded-lg shadow-lg"
      >
        <h2 className="text-xl font-semibold mb-4">{special ? 'Edit Special' : 'Add New Special'}</h2>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700">Special Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Special Price</label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiDollarSign className="text-gray-400" />
              </div>
              <input
                type="number"
                name="specialPrice"
                value={formData.specialPrice}
                onChange={handleChange}
                className="block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                placeholder="0.00"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Start Date</label>
            <DatePicker
              selected={formData.startDate}
              onChange={(date) => setFormData({ ...formData, startDate: date })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">End Date</label>
            <DatePicker
              selected={formData.endDate}
              onChange={(date) => setFormData({ ...formData, endDate: date })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            />
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Items</label>
          <div className="grid grid-cols-2 gap-2">
            {items.map((item) => (
              <div key={item.id} className="flex items-center">
                <input
                  type="checkbox"
                  id={`item-${item.id}`}
                  checked={formData.items.includes(item.name)}
                  onChange={() => handleItemToggle(item.name)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor={`item-${item.id}`} className="ml-2 block text-sm text-gray-900">
                  {item.name}
                </label>
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-between items-center mb-4">
          <button
            type="button"
            onClick={handleCalculate}
            className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600"
          >
            Calculate Metrics
          </button>
          {calculatedMetrics && (
            <div>
              <p className="text-sm">Regular Price: ${calculatedMetrics.regularPrice.toFixed(2)}</p>
              <p className="text-sm">Cost: ${calculatedMetrics.cost.toFixed(2)}</p>
              <p className="text-sm">Discount: {calculatedMetrics.discount.toFixed(2)}%</p>
              <p className="text-sm">Margin: {calculatedMetrics.margin.toFixed(2)}%</p>
            </div>
          )}
        </div>
        <div className="flex justify-end space-x-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            {special ? 'Update Special' : 'Add Special'}
          </button>
        </div>
      </motion.form>
    );
  };

  const handleAddSpecial = (newSpecial) => {
    setSpecials([...specials, { ...newSpecial, id: specials.length + 1 }]);
    setIsAddingSpecial(false);
  };

  const handleUpdateSpecial = (updatedSpecial) => {
    setSpecials(specials.map(special => special.id === updatedSpecial.id ? updatedSpecial : special));
    setSelectedSpecial(null);
  };

  const handleDeleteSpecial = (id) => {
    setSpecials(specials.filter(special => special.id !== id));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Specials and Combos</h2>
        <button 
          onClick={() => setIsAddingSpecial(true)}
          className="bg-green-500 text-white px-4 py-2 rounded-full hover:bg-green-600 transition-colors duration-300 flex items-center"
        >
          <FiPlus className="mr-2" /> Add New Special
        </button>
      </div>

      <AnimatePresence>
        {(isAddingSpecial || selectedSpecial) && (
          <SpecialForm 
            special={selectedSpecial} 
            onSubmit={selectedSpecial ? handleUpdateSpecial : handleAddSpecial} 
            onCancel={() => {
              setSelectedSpecial(null);
              setIsAddingSpecial(false);
            }}
          />
        )}
      </AnimatePresence>

      <motion.div layout className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <AnimatePresence>
          {specials.map(special => (
            <SpecialCard 
              key={special.id} 
              special={special} 
              onEdit={setSelectedSpecial}
              onDelete={handleDeleteSpecial}
            />
          ))}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default SpecialsTab;